import React, { useState } from 'react';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { PenLine } from 'lucide-react';
import FormModalLayout from '../Common/FormModalLayout';
import { licenseService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { isTotpRequired } from '../../utils/authorization';

function signErrorMessage(error) {
  const status = error?.response?.status;
  if (status === 401) return 'Senha ou código incorretos.';
  if (status === 423) return 'Muitas tentativas. Aguarde alguns minutos e tente de novo.';
  return userFacingError(error, 'saveError');
}

/** Assinatura eletrônica com o login de quem assina: revisa o conteúdo e confirma a senha. */
const AuthorizationSignModal = ({ authorization, template, onClose, onSigned }) => {
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [askCode, setAskCode] = useState(false);

  const sign = useMutation(
    () => licenseService.signAuthorization(authorization.id, { senha, codigo: askCode ? codigo : null }),
    {
      onSuccess: (row) => {
        toast.success(row?.numero ? `Autorização ${row.numero} emitida.` : 'Assinatura registrada.');
        if (onSigned) onSigned(row);
        onClose();
      },
      onError: (error) => {
        if (isTotpRequired(error)) {
          setAskCode(true);
          return;
        }
        toast.error(signErrorMessage(error));
      },
    }
  );

  const conditions = authorization.condicionantes || [];
  const ready = senha.length > 0 && (!askCode || codigo.trim().length >= 6);

  return (
    <FormModalLayout
      title={`Assinar ${template?.titulo || 'autorização'}`}
      subtitle="Depois da primeira assinatura o conteúdo não pode mais ser alterado."
      headerIcon={PenLine}
      onClose={onClose}
      size="xl"
      footer={
        <button type="button" className="btn btn-primary" disabled={!ready || sign.isLoading} onClick={() => sign.mutate()}>
          {sign.isLoading ? 'Assinando…' : 'Assinar eletronicamente'}
        </button>
      }
    >
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
        <p className="whitespace-pre-wrap">{authorization.texto}</p>
        <p className="mt-2 font-medium">Validade: {authorization.validade_dias} dias a partir da emissão</p>
        {conditions.length ? (
          <ul className="mt-2 list-disc pl-5 text-slate-700">
            {conditions.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        ) : null}
      </div>
      <label htmlFor="assinatura-senha" className="form-label mt-4">
        Sua senha
      </label>
      <input
        id="assinatura-senha"
        type="password"
        autoComplete="current-password"
        className="form-input"
        value={senha}
        onChange={(e) => setSenha(e.target.value)}
      />
      {askCode ? (
        <>
          <label htmlFor="assinatura-codigo" className="form-label mt-3">
            Código do autenticador
          </label>
          <input
            id="assinatura-codigo"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="form-input"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
          />
        </>
      ) : null}
      <p className="mt-2 text-xs text-slate-500">
        O documento sairá com “Assinado eletronicamente por” seu nome e cargo, data e hora, e um código com QR para
        conferência pública.
      </p>
    </FormModalLayout>
  );
};

export default AuthorizationSignModal;
